import { Component, inject, Input } from '@angular/core';
import { FormGroup, FormControl, Validators, ReactiveFormsModule, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { ICategory } from '../../../category/model/category.model';
import { CategoryService } from '../../../category/services/category.service';
import { environment } from '../../../environments/environment';
import { stripeAccount } from '../../../financials/model/stripe-account.model';
import { BillingService } from '../../../financials/services/billing.service';
import { ToastService } from '../../../shared/services/toast.service';
import { IUser } from '../../../user/model/user.model';
import { ICourse } from '../../models/course.model';
import { ICourseStatus } from '../../models/courseStatus.model';
import { CourseService } from '../../services/course.service';
import { DatePipe } from '@angular/common';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ToastrService } from 'ngx-toastr';
import { CalendarModule } from 'primeng/calendar';
import { ImageUploadComponent } from '../../../shared/components/file-upload/file-upload.component';
import { LanguageService } from '../../../shared/services/language.services';
import { Store } from '@ngrx/store';
import { getCurrentCourseSelector } from '../../store/course.selectors';
import { PrimeNGConfig } from 'primeng/api';


export type GeneralDataSection = 'all' | 'generalDescription' | 'image' | 'financial' | 'calendar';

@Component({
    selector: 'clp-course-general-data',
    templateUrl: './courseGeneralData.component.html',
    styleUrls: ['./courseGeneralData.component.scss'],
    imports: [
    RouterModule,
    TranslateModule,
    ReactiveFormsModule,
    CalendarModule,
    ImageUploadComponent
],
    providers: [
        CategoryService,
        CourseService,
        ToastrService,
        DatePipe,
        BrowserAnimationsModule,
        LanguageService,
        BillingService
    ]
})
export class GeneralDataComponent {
  private courseService = inject(CourseService);
  private toastService = inject(ToastService);
  private translate = inject(TranslateService);
  private route = inject(ActivatedRoute);
  private store = inject(Store);
  private billingService = inject(BillingService);
  private categoryService = inject(CategoryService);
  private config = inject(PrimeNGConfig);


  ICourseStatus = ICourseStatus;
  courseStatuses: { value: ICourseStatus, label: string }[] = [];
  categories$: ICategory[] = [];
  courseForm!: FormGroup;
  course$!: Observable<ICourse>;
  id!: string | null;
  edit: boolean = false;
  user$!: Observable<IUser >;
  imageUrl: string | undefined;
  currentImageFile?: string;
  account$!: Observable<stripeAccount>;
  account!: stripeAccount;
  @Input() section: GeneralDataSection = 'all';

  ngOnInit(): void {
      this.id = this.route.snapshot.paramMap.get('id');
      this.edit = this.id !== undefined && this.id !== null && this.id !== '';

      this.imageUrl = undefined;
      this.currentImageFile = undefined;

      this.initializeForm();
      this.loadCourseData();
      this.loadCategories();
      this.initializeCourseStatuses();
      this.setPrimeNGConfig();
      this.getStripeAccount();
  }

  initializeForm(): void {
      this.courseForm = new FormGroup({
          id: new FormControl(''),
          title: new FormControl('', [Validators.required, Validators.maxLength(50)]),
          headline: new FormControl('', [Validators.required, Validators.maxLength(80)]),
          launchDate: new FormControl('', Validators.required),
          hours: new FormControl('', [Validators.required, Validators.min(0)]),
          //limitedDaysAccess: new FormControl('', [Validators.required, Validators.min(0)]),
          limitedDaysAccess: new FormControl(''),
          courseUrl: new FormControl(''),
          order: new FormControl('', [Validators.required, Validators.min(0)]),
          instructor: new FormControl(''),
          instructorPicture: new FormControl(''),
          discountPrice: new FormControl('', [Validators.required, Validators.min(0)]),
          fullPrice: new FormControl('', [Validators.required , Validators.min(0)]),
          currency: new FormControl('', Validators.required),
          installments: new FormControl('', [Validators.required, Validators.min(0)]),
          salesStartDate: new FormControl('', Validators.required),
          salesEndDate: new FormControl('', Validators.required),
          status: new FormControl('', Validators.required),
          picture: new FormControl(''),
          cover: new FormControl(''),
          video: new FormControl(''),
          summary: new FormControl('', Validators.required),
          about: new FormControl('', Validators.required),
          modules: new FormControl(''),
          issueCertificate: new FormControl(false)
      }, { validators: salesDateValidator });
  }

  loadCourseData(): void {
      if (this.edit) {
          this.course$ = this.store.select(getCurrentCourseSelector);
          this.course$.subscribe(course => {
              this.courseForm.patchValue({
                  ...course,
                  launchDate: course.launchDate ? new Date(course.launchDate) : null,
                  salesStartDate: course.salesStartDate ? new Date(course.salesStartDate) : null,
                  salesEndDate: course.salesEndDate ? new Date(course.salesEndDate) : null
              });
              if (course.picture !== undefined && course.picture !== null) {
                  this.imageUrl = environment.API_URL + '/posts/images/' + course.picture;
                  this.currentImageFile = course.picture;
              }
          });
      }
  }

  loadCategories(): void {
      this.categoryService.getCategories().subscribe((categories: ICategory[]) => {
          this.categories$ = categories;
      });
  }

  initializeCourseStatuses(): void {
      this.courseStatuses = Object.entries(ICourseStatus)
          .filter(([key, value]) => typeof value === 'number')
          .map(([key, value]) => ({ value: value as ICourseStatus, label: `course.enum.${key}` }));
  }

  setPrimeNGConfig(): void {
    this.config.setTranslation({
        accept: this.translate.instant("general.calendar.accept"),
        reject: this.translate.instant("general.calendar.reject"),
        dayNames: this.translate.instant("general.calendar.dayNames"),
        dayNamesShort: this.translate.instant("general.calendar.dayNamesShort"),
        dayNamesMin: this.translate.instant("general.calendar.dayNamesMin"),
        monthNames: this.translate.instant("general.calendar.monthNames"),
        monthNamesShort: this.translate.instant("general.calendar.monthNamesShort"),
        today: this.translate.instant("general.calendar.today"),
        clear: this.translate.instant("general.calendar.clear"),
        dateFormat: this.translate.instant("general.calendar.dateFormat"),
        weekHeader: this.translate.instant("general.calendar.weekHeader")
    });
  }

  getStripeAccount(): void {
      this.account$ = this.billingService.getStripeAccount();
      this.account$.subscribe(x => this.account = x);
  }

  saveCourse(): boolean {
      const formValues = this.courseForm.value;
      if (this.courseForm.valid) {
          this.changeCourse(formValues);
          return true;
      } else {
          this.courseForm.markAllAsTouched();
          return false;
      }
  }

  changeCourse(formValues: any): void {
      if (!this.courseForm.invalid) {
          const course: ICourse = {
              ...formValues,
              id: this.id,
              picture: this.currentImageFile,
              limitedDaysAccess: formValues.limitedDaysAccess !== undefined &&
                                  formValues.limitedDaysAccess !== null &&
                                  formValues.limitedDaysAccess !== '' ? formValues.limitedDaysAccess : null
          };
          if (this.validateStripeSetup(course.status as ICourseStatus)) {
              let return$: Observable<ICourse>;
              if (!this.edit) {
                  return$ = this.courseService.createCourse(course);
              } else {
                  return$ = this.courseService.editCourse(course);
              }

              return$.subscribe(course => {
                  if (course?.id) {
                      const message = this.edit ? 'course.update.successMessage' : 'course.create.successMessage';
                      this.toastService.show(this.translate.instant(message), '');
                  }
              });
          } else {
              this.toastService.error(this.translate.instant('course.stripeAccountNotSet'), '');
          }
      } else {
          this.courseForm.markAllAsTouched();
      }
    }

    deleteImage(): void {
      this.currentImageFile = undefined;
    }

    pictureChanged(file: File | null): void {
      this.courseService.changePicture(file, this.id!).subscribe(
          image => {
              this.currentImageFile = image;
          }
      );
    }

    validateStripeSetup(status: ICourseStatus): boolean {
        if (status == ICourseStatus.Published && (this.account?.id === undefined || this.account?.id === null)) {
            return false;
        } else {
            return true;
        }
    }

    showSection(section: Exclude<GeneralDataSection, 'all'>): boolean {
      return this.section === 'all' || this.section === section;
    }

}


export const salesDateValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const salesStartDateDate = control.get('salesStartDate');
  const salesEndDateDate = control.get('salesEndDate');

  if (salesStartDateDate && salesEndDateDate) {
    const startDate = new Date(salesStartDateDate.value);
    const endDate = new Date(salesEndDateDate.value);

    if (startDate > endDate) {
      return { salesDateInvalid: true };
    }
  }

  return null;
};
